/**
 * Anything · the true type-anything pillar (founder-approved 18 Jul 2026).
 *
 * Flow: one free-text ask → (optionally ONE row of tappable chips, never a
 * second text box) → concierge-voice intro over ~5 curated cards. The member
 * never sees where a find came from — "Concierge® arrange it" carries the
 * provenance to the desk's Zoho ticket server-side.
 */
import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Camera } from "lucide-react";
import { useEmbedMember } from "@/hooks/useEmbedMember";
import MemberContextBand from "../components/MemberContextBand";
import CrossSurfaceNav from "@/components/global/CrossSurfaceNav";
import ConciergeSurfaceMenu from "@/components/global/ConciergeSurfaceMenu";

const API = process.env.REACT_APP_BACKEND_URL;

const T = {
  bg:   "#F4EFE6", card: "#FCFAF4", ink:  "#211C15",
  ink2: "#5C5447", ink3: "#8A8071",
  line: "#E5DCCB", line2:"#D8CDB7",
  brass:"#8B6914", brassSoft: "#B69152",
};

const SAMPLE_ASKS = [
  "Flowers for every occasion",
  "Surprise someone — the house composes it",
  "Someone to handle my UK priority visa",
  "A birthday gift for a 1-year-old",
  "Best cake shop in Bandra",
  "Something special to do tonight in Delhi",
];

const LOADING_LINES = [
  "Consulting the house…",
  "Asking around the right circles…",
  "Curating the shortlist…",
  "Almost there — final judgment…",
];

const KIND_LABEL = { shopping: "the find", local: "the address", event: "the evening",
                     service: "the specialist", pillar: "the house",
                     house: "the golden book · house find" };

export default function AnythingPage() {
  const [stage, setStage]           = useState("ask"); // ask | loading | chips | answer
  const [query, setQuery]           = useState("");
  const [sessionId, setSessionId]   = useState(null);
  const [chipGroups, setChipGroups] = useState([]);
  const [chipPicks, setChipPicks]   = useState({});
  const [assumptions, setAssumptions] = useState({});
  const [intro, setIntro]           = useState("");
  const [headline, setHeadline]     = useState(null);
  const [cards, setCards]           = useState([]);
  const [selected, setSelected]     = useState({}); // rec_id → card
  const [error, setError]           = useState(null);
  const [arranging, setArranging]   = useState(null); // card being arranged
  const [detail, setDetail]         = useState(null); // card in detail sheet
  const [hasMore, setHasMore]       = useState(false);
  const [moreLoading, setMoreLoading] = useState(false);
  const [gesture, setGesture]       = useState(false);
  const [impossible, setImpossible] = useState(null); // query being escalated
  const [toast, setToast]           = useState(null);
  const [lensPreview, setLensPreview] = useState(null); // data URL of the sent photo
  const lensInputRef = useRef(null);
  const embedMember = useEmbedMember();
  const [recent, setRecent] = useState(() => {
    try { return JSON.parse(localStorage.getItem("lc_anything_recent_v1")) || []; }
    catch (_) { return []; }
  });

  const remember = (q) => {
    const next = [{ q, at: Date.now() },
                  ...recent.filter((r) => r.q.toLowerCase() !== q.toLowerCase())].slice(0, 8);
    setRecent(next);
    try { localStorage.setItem("lc_anything_recent_v1", JSON.stringify(next)); } catch (_) {}
  };

  const ask = async (q, chips = null, sid = null) => {
    setStage("loading");
    setError(null);
    try {
      const history = recent
        .filter((r) => r.q.toLowerCase() !== q.toLowerCase())
        .slice(0, 5).map((r) => r.q);
      const r = await fetch(`${API}/api/anything/ask`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ query: q, chips, session_id: sid,
                               history: history.length ? history : null }),
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j?.detail || "failed");
      setSessionId(j.session_id);
      if (j.stage === "chips") {
        setChipGroups(j.chips || []);
        setAssumptions(j.assumptions || {});
        setChipPicks({});
        setStage("chips");
      } else {
        setIntro(j.intro || "");
        setHeadline(j.headline || null);
        setCards(j.cards || []);
        setAssumptions(j.assumptions || {});
        setHasMore(!!j.has_more);
        setGesture(!!j.gesture);
        setStage("answer");
      }
    } catch (_) {
      setError("The house couldn't take that just now — please try again.");
      setStage("ask");
    }
  };

  // Google Lens · downscale client-side (≤1280px JPEG) so the payload
  // stays well under proxy limits, then let the house look at it.
  const fileToDataUrl = (file) => new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      const max = 1280;
      const scale = Math.min(1, max / Math.max(img.width, img.height));
      const canvas = document.createElement("canvas");
      canvas.width = Math.max(1, Math.round(img.width * scale));
      canvas.height = Math.max(1, Math.round(img.height * scale));
      canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
      URL.revokeObjectURL(url);
      resolve(canvas.toDataURL("image/jpeg", 0.85));
    };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error("bad image")); };
    img.src = url;
  });

  const askLens = async (file) => {
    if (!file || stage === "loading") return;
    setStage("loading");
    setError(null);
    try {
      const dataUrl = await fileToDataUrl(file);
      setLensPreview(dataUrl);
      const caption = query.trim();
      const r = await fetch(`${API}/api/anything/lens`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ image_b64: dataUrl,
                               caption: caption.length >= 3 ? caption : null }),
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j?.detail || "failed");
      setSessionId(j.session_id);
      setIntro(j.intro || "");
      setHeadline(j.headline || null);
      setCards(j.cards || []);
      setAssumptions({});
      setHasMore(!!j.has_more);
      setGesture(false);
      setStage("answer");
    } catch (_) {
      setError("The house couldn't read that photo just now — please try again.");
      setLensPreview(null);
      setStage("ask");
    }
  };

  const showMore = async () => {
    if (!sessionId || moreLoading) return;
    setMoreLoading(true);
    try {
      const r = await fetch(`${API}/api/anything/more`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId }),
      });
      const j = await r.json();
      if (r.ok) {
        setCards((prev) => [...prev, ...(j.cards || [])]);
        setHasMore(!!j.has_more);
      }
    } catch (_) { /* keep the button · member can retry */ }
    setMoreLoading(false);
  };

  const reset = () => {
    setStage("ask"); setQuery(""); setSessionId(null);
    setChipGroups([]); setChipPicks({}); setAssumptions({});
    setIntro(""); setCards([]); setError(null);
    setHasMore(false); setDetail(null);
    setHeadline(null); setLensPreview(null);
    setGesture(false); setImpossible(null);
    // NOTE: `selected` (the running brief) deliberately survives resets —
    // members add one more, and one more, across searches.
  };

  const toggleSelect = (card) => {
    setSelected((prev) => {
      const next = { ...prev };
      if (next[card.rec_id]) delete next[card.rec_id];
      else next[card.rec_id] = card;
      return next;
    });
  };

  return (
    <div data-testid="anything-page"
         style={{ background: T.bg, minHeight: "100vh", color: T.ink,
                  fontFamily: "'Fraunces', Georgia, serif", paddingBottom: 80 }}>
      <header data-testid="anything-header"
              style={{ borderBottom: `1px solid ${T.line}`, background: T.bg,
                       position: "sticky", top: 0, zIndex: 20 }}>
        <div style={{ maxWidth: 1180, margin: "0 auto", display: "flex",
                      alignItems: "center", justifyContent: "space-between",
                      padding: "14px 22px", gap: 10, minWidth: 0 }}>
          <Link to="/" data-testid="anything-home-button"
                style={{ textDecoration: "none", color: T.ink, display: "inline-flex",
                         alignItems: "baseline", gap: 10, minWidth: 0 }}>
            <span className="t-serif" style={{ fontSize: 22, fontWeight: 500,
                                               letterSpacing: "0.02em" }}>
              /anything
            </span>
            <span className="lc-mobile-hide-on-narrow"
                  style={{ fontFamily: "ui-monospace, monospace", fontSize: 11,
                           letterSpacing: "0.22em", textTransform: "uppercase",
                           color: T.ink3, whiteSpace: "nowrap" }}>
              by Concierge®
            </span>
          </Link>
          <div style={{ display: "flex", alignItems: "center", gap: 10, flexShrink: 0 }}>
            <CrossSurfaceNav testidPrefix="anything-header-nav" size="compact" />
            <ConciergeSurfaceMenu testid="anything-surface-menu" />
          </div>
        </div>
      </header>
      <MemberContextBand pillar="anything" />

      {/* ── Hero + ask box ── */}
      <section style={{ maxWidth: 920, margin: "0 auto", padding: "56px 32px 24px" }}>
        <p style={{ letterSpacing: "0.16em", fontSize: 11, color: T.brass,
                    textTransform: "uppercase", margin: "0 0 12px", fontWeight: 700,
                    fontFamily: "'Inter', sans-serif" }}>
          /anything · by the Concierge®
        </p>
        <h1 style={{ fontSize: "clamp(30px, 5vw, 52px)", lineHeight: 1.08,
                     fontWeight: 500, margin: "0 0 14px" }}>
          Ask for anything. <em style={{ color: T.brass }}>The house will find it.</em>
        </h1>
        <p style={{ color: T.ink2, fontSize: 17, lineHeight: 1.55, maxWidth: 620,
                    margin: 0, fontFamily: "'Inter', sans-serif" }}>
          A gift, an address, an evening, a specialist to handle it, a
          hard-to-find thing — type it the way you'd say it. We'll bring
          back a short, considered list.
        </p>

        {/* Persistent ask bar · stays visible after an answer so the
            member can go again without hunting for a reset button. */}
        <form
          onSubmit={(e) => {
            e.preventDefault();
            const q = query.trim();
            if (q.length >= 3 && stage !== "loading") { remember(q); ask(q); }
          }}
          style={{ display: "flex", gap: 12, flexWrap: "wrap", marginTop: 28 }}>
          <input
            data-testid="anything-ask-input"
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            disabled={stage === "loading"}
            placeholder="e.g., A thoughtful birthday gift for a 1-year-old, in Mumbai"
            maxLength={400}
            style={{ flex: 1, minWidth: 280, padding: "16px 20px", fontSize: 16,
                     borderRadius: 999, border: `1px solid ${T.line2}`,
                     background: T.card, color: T.ink,
                     opacity: stage === "loading" ? 0.6 : 1,
                     fontFamily: "'Inter', sans-serif", outline: "none" }}
          />
          <input
            ref={lensInputRef}
            data-testid="anything-lens-input"
            type="file"
            accept="image/*"
            style={{ display: "none" }}
            onChange={(e) => {
              const f = e.target.files && e.target.files[0];
              e.target.value = "";
              if (f) askLens(f);
            }}
          />
          <button
            data-testid="anything-lens-button"
            type="button"
            disabled={stage === "loading"}
            onClick={() => lensInputRef.current && lensInputRef.current.click()}
            title="Send a photo — the house will find it"
            aria-label="Search with a photo"
            style={{ width: 54, height: 54, borderRadius: 999, flexShrink: 0,
                     border: `1px solid ${T.line2}`, background: T.card,
                     color: T.brass, cursor: stage === "loading" ? "not-allowed" : "pointer",
                     display: "inline-flex", alignItems: "center",
                     justifyContent: "center",
                     opacity: stage === "loading" ? 0.5 : 1,
                     touchAction: "manipulation" }}>
            <Camera size={22} strokeWidth={1.6} />
          </button>
          <button
            data-testid="anything-ask-submit"
            type="submit"
            disabled={query.trim().length < 3 || stage === "loading"}
            onMouseDown={(e) => e.preventDefault()}
            style={{ padding: "16px 28px", fontSize: 14, fontWeight: 600,
                     background: T.ink, color: T.bg, border: 0, borderRadius: 999,
                     cursor: query.trim().length < 3 || stage === "loading" ? "not-allowed" : "pointer",
                     opacity: query.trim().length < 3 || stage === "loading" ? 0.45 : 1,
                     letterSpacing: "0.06em", textTransform: "uppercase",
                     touchAction: "manipulation",
                     fontFamily: "'Inter', sans-serif" }}>
            Ask the house →
          </button>
        </form>

        {stage === "ask" && (
          <>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 16,
                          fontFamily: "'Inter', sans-serif" }}>
              {SAMPLE_ASKS.map((s) => (
                <button key={s} type="button"
                        data-testid={`anything-sample-${s.slice(0, 12).replace(/\W/g, "")}`}
                        onClick={() => { setQuery(s); remember(s); ask(s); }}
                        style={{ background: "transparent", border: `1px solid ${T.line}`,
                                 borderRadius: 999, padding: "7px 14px", fontSize: 12.5,
                                 color: T.ink2, cursor: "pointer" }}>
                  {s}
                </button>
              ))}
            </div>
            {recent.length > 0 && (
              <div data-testid="anything-recent-asks" style={{ marginTop: 24 }}>
                <p style={{ letterSpacing: "0.14em", fontSize: 10, color: T.brass,
                            textTransform: "uppercase", fontWeight: 700,
                            margin: "0 0 8px", fontFamily: "'Inter', sans-serif" }}>
                  The house remembers — you've asked before
                </p>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 8,
                              fontFamily: "'Inter', sans-serif" }}>
                  {recent.map((r) => (
                    <button key={r.q} type="button"
                            data-testid="anything-recent-chip"
                            onClick={() => { setQuery(r.q); remember(r.q); ask(r.q); }}
                            style={{ background: T.card, border: `1px solid ${T.line2}`,
                                     borderRadius: 999, padding: "7px 14px",
                                     fontSize: 12.5, color: T.ink, cursor: "pointer" }}>
                      {r.q.length > 60 ? r.q.slice(0, 57) + "…" : r.q}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {error && (
              <p data-testid="anything-error" style={{ color: "#8E3B2F", fontSize: 14,
                   marginTop: 16, fontFamily: "'Inter', sans-serif" }}>{error}</p>
            )}
          </>
        )}
      </section>

      {lensPreview && (stage === "loading" || stage === "answer") && (
        <div data-testid="anything-lens-preview"
             style={{ maxWidth: 920, margin: "0 auto", padding: "0 32px 8px",
                      display: "flex", alignItems: "center", gap: 12,
                      fontFamily: "'Inter', sans-serif" }}>
          <img src={lensPreview} alt="Your photo"
               style={{ width: 52, height: 52, borderRadius: 10,
                        objectFit: "cover", border: `1px solid ${T.line2}` }} />
          <span style={{ fontSize: 12.5, color: T.ink3, letterSpacing: "0.06em",
                         textTransform: "uppercase", fontWeight: 600 }}>
            {stage === "loading" ? "The house is looking at your photo…"
                                 : "Found from your photo"}
          </span>
        </div>
      )}

      {stage === "loading" && <LoadingBand />}

      {stage === "chips" && (
        <ChipsStage
          query={query}
          groups={chipGroups}
          picks={chipPicks}
          setPicks={setChipPicks}
          assumptions={assumptions}
          onSubmit={(chips) => ask(query.trim(), chips, sessionId)}
        />
      )}

      {stage === "answer" && (
        <AnswerStage
          intro={intro}
          headline={headline}
          cards={cards}
          assumptions={assumptions}
          hasMore={hasMore}
          moreLoading={moreLoading}
          gesture={gesture}
          selected={selected}
          onToggleSelect={toggleSelect}
          onMore={showMore}
          onDetail={(card) => setDetail(card)}
          onArrange={(card) => setArranging([card])}
          onArrangeAll={(all) => setArranging(all)}
          onImpossible={() => setImpossible(query.trim()
            || (lensPreview ? "Find this — the member sent a photo" : ""))}
          onReset={reset}
        />
      )}

      {stage === "answer" && Object.keys(selected).length > 0 && !arranging && !detail && (
        <div data-testid="anything-multi-bar"
             style={{ position: "fixed", bottom: 24, left: "50%",
                      transform: "translateX(-50%)", background: T.ink,
                      color: T.bg, borderRadius: 999, padding: "12px 14px 12px 24px",
                      display: "flex", alignItems: "center", gap: 14, zIndex: 70,
                      boxShadow: "0 12px 32px rgba(33,28,21,0.32)",
                      fontFamily: "'Inter', sans-serif", maxWidth: "92vw" }}>
          <span style={{ fontSize: 13.5 }}>
            {Object.keys(selected).length} pick{Object.keys(selected).length > 1 ? "s" : ""} in your brief
          </span>
          <button
            data-testid="anything-multi-arrange"
            type="button"
            onClick={() => setArranging(Object.values(selected))}
            style={{ background: T.brassSoft, color: T.ink, border: 0,
                     borderRadius: 999, padding: "10px 20px", fontSize: 12.5,
                     fontWeight: 700, letterSpacing: "0.06em",
                     textTransform: "uppercase", cursor: "pointer" }}>
            Concierge® arrange all →
          </button>
          <button
            data-testid="anything-multi-clear"
            type="button" onClick={() => setSelected({})}
            aria-label="Clear selection"
            style={{ background: "transparent", color: T.bg, border: 0,
                     fontSize: 17, cursor: "pointer", opacity: 0.7, lineHeight: 1 }}>
            ×
          </button>
        </div>
      )}

      {detail && (
        <DetailSheet
          card={detail}
          siblings={cards.filter((c) => c.rec_id !== detail.rec_id && c.kind === detail.kind).slice(0, 4)}
          onSwitch={(card) => setDetail(card)}
          onClose={() => setDetail(null)}
          onArrange={(card) => { setDetail(null); setArranging([card]); }}
        />
      )}

      {arranging && (
        <ArrangeModal
          cards={arranging}
          embedMember={embedMember}
          onClose={() => setArranging(null)}
          onSuccess={(ref) => {
            setArranging(null);
            setSelected({});
            setToast({ ref });
            setTimeout(() => setToast(null), 6000);
          }}
          onError={(msg) => setToast({ error: msg })}
        />
      )}

      {impossible && (
        <ImpossibleModal
          query={impossible}
          embedMember={embedMember}
          onClose={() => setImpossible(null)}
          onSuccess={(ref) => {
            setImpossible(null);
            setToast({ ref, impossible: true });
            setTimeout(() => setToast(null), 8000);
          }}
          onError={(msg) => setToast({ error: msg })}
        />
      )}

      {toast && (
        <div data-testid={toast.error ? "anything-toast-error" : "anything-toast-success"}
             role="status"
             style={{ position: "fixed", bottom: 28, left: "50%",
                      transform: "translateX(-50%)", background: T.ink, color: T.bg,
                      borderRadius: 12, padding: "16px 24px", fontSize: 14,
                      boxShadow: "0 12px 32px rgba(33,28,21,0.28)", zIndex: 120,
                      maxWidth: "92vw", fontFamily: "'Inter', sans-serif" }}>
          {toast.error ? toast.error : toast.impossible ? (
            <>
              <div style={{ color: T.brassSoft, letterSpacing: "0.14em", fontSize: 10,
                            textTransform: "uppercase", fontWeight: 700, marginBottom: 4 }}>
                The desk has it — personally
              </div>
              A human is on your ask. Expect a WhatsApp within the hour. Reference:{" "}
              <span style={{ fontFamily: "ui-monospace, Menlo, monospace", fontSize: 12 }}>
                {toast.ref}
              </span>
            </>
          ) : (
            <>
              <div style={{ color: T.brassSoft, letterSpacing: "0.14em", fontSize: 10,
                            textTransform: "uppercase", fontWeight: 700, marginBottom: 4 }}>
                Concierge® has it
              </div>
              We'll reach out on WhatsApp shortly. Reference:{" "}
              <span style={{ fontFamily: "ui-monospace, Menlo, monospace", fontSize: 12 }}>
                {toast.ref}
              </span>
            </>
          )}
        </div>
      )}
    </div>
  );
}

// ── Loading band ────────────────────────────────────────────────
function LoadingBand() {
  const [i, setI] = useState(0);
  const timer = useRef(null);
  useEffect(() => {
    timer.current = setInterval(() => setI((v) => Math.min(v + 1, LOADING_LINES.length - 1)), 4500);
    return () => clearInterval(timer.current);
  }, []);
  return (
    <section data-testid="anything-loading"
             style={{ maxWidth: 920, margin: "0 auto", padding: "48px 32px",
                      textAlign: "left" }}>
      <div style={{ display: "inline-flex", alignItems: "center", gap: 14 }}>
        <span aria-hidden="true" style={{
          width: 10, height: 10, borderRadius: "50%", background: T.brass,
          animation: "lc-anything-pulse 1.2s ease-in-out infinite" }} />
        <span style={{ fontSize: 20, color: T.ink2, fontStyle: "italic" }}>
          {LOADING_LINES[i]}
        </span>
      </div>
      <style>{`@keyframes lc-anything-pulse {
        0%,100% { opacity: .35; transform: scale(.85); }
        50%     { opacity: 1;   transform: scale(1.15); } }`}</style>
    </section>
  );
}

// ── Chips stage · ONE row of tappable answers, never a text box ──
function ChipsStage({ query, groups, picks, setPicks, assumptions, onSubmit }) {
  const assumptionLine = Object.entries(assumptions || {})
    .map(([k, v]) => `${k.replace(/_/g, " ")}: ${v}`).join(" · ");
  return (
    <section data-testid="anything-chips-stage"
             style={{ maxWidth: 920, margin: "0 auto", padding: "24px 32px" }}>
      <div style={{ background: T.card, border: `1px solid ${T.line}`,
                    borderRadius: 16, padding: "26px 28px" }}>
        <p style={{ letterSpacing: "0.14em", fontSize: 10, color: T.brass,
                    textTransform: "uppercase", margin: "0 0 6px", fontWeight: 700,
                    fontFamily: "'Inter', sans-serif" }}>
          One quick thing
        </p>
        <h2 style={{ fontSize: 22, fontWeight: 500, margin: "0 0 18px", lineHeight: 1.2 }}>
          “{query}” — a touch more and we'll nail it.
        </h2>
        {groups.map((g) => (
          <div key={g.key} style={{ marginBottom: 16 }}>
            <p style={{ letterSpacing: "0.12em", fontSize: 10.5, color: T.ink3,
                        textTransform: "uppercase", margin: "0 0 8px", fontWeight: 700,
                        fontFamily: "'Inter', sans-serif" }}>
              {g.label}
            </p>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8,
                          fontFamily: "'Inter', sans-serif" }}>
              {(g.options || []).map((opt) => {
                const active = picks[g.key] === opt;
                return (
                  <button key={opt} type="button"
                          data-testid={`anything-chip-${g.key}-${opt.replace(/\W/g, "")}`}
                          onClick={() => setPicks({ ...picks, [g.key]: active ? undefined : opt })}
                          style={{ background: active ? T.ink : T.card,
                                   color: active ? T.bg : T.ink,
                                   border: `1px solid ${active ? T.ink : T.line2}`,
                                   borderRadius: 999, padding: "9px 18px",
                                   fontSize: 13, fontWeight: 500, cursor: "pointer" }}>
                    {opt}
                  </button>
                );
              })}
            </div>
          </div>
        ))}
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginTop: 20,
                      alignItems: "center", fontFamily: "'Inter', sans-serif" }}>
          <button
            data-testid="anything-chips-submit"
            type="button"
            onClick={() => {
              const clean = Object.fromEntries(
                Object.entries(picks).filter(([, v]) => v));
              onSubmit(clean);
            }}
            style={{ background: T.ink, color: T.bg, border: 0, borderRadius: 999,
                     padding: "13px 26px", fontSize: 13, fontWeight: 600,
                     letterSpacing: "0.06em", textTransform: "uppercase",
                     cursor: "pointer" }}>
            Show me →
          </button>
          <button
            data-testid="anything-chips-skip"
            type="button"
            onClick={() => onSubmit({})}
            style={{ background: "transparent", color: T.ink2,
                     border: `1px solid ${T.line2}`, borderRadius: 999,
                     padding: "13px 22px", fontSize: 13, cursor: "pointer" }}>
            Skip — the house decides
          </button>
          {assumptionLine && (
            <span data-testid="anything-chips-assumptions"
                  style={{ fontSize: 12, color: T.ink3, fontStyle: "italic" }}>
              If you skip, we'll assume {assumptionLine}.
            </span>
          )}
        </div>
      </div>
    </section>
  );
}

// ── Answer stage · intro + curated cards ────────────────────────
function AnswerStage({ intro, headline, cards, assumptions, hasMore, moreLoading,
                       gesture, selected, onToggleSelect, onMore, onDetail,
                       onArrange, onArrangeAll, onImpossible, onReset }) {
  const assumptionLine = Object.entries(assumptions || {})
    .map(([k, v]) => `${k.replace(/_/g, " ")}: ${v}`).join(" · ");
  return (
    <section data-testid="anything-answer-stage"
             style={{ maxWidth: 1160, margin: "0 auto", padding: "12px 32px" }}>
      {headline && (
        <p data-testid="anything-headline"
           style={{ letterSpacing: "0.18em", fontSize: 12, color: T.brass,
                    textTransform: "uppercase", fontWeight: 700,
                    margin: "10px 0 2px", fontFamily: "'Inter', sans-serif" }}>
          ✦ {headline}
        </p>
      )}      {intro && (
        <p data-testid="anything-intro"
           style={{ fontSize: 20, lineHeight: 1.5, color: T.ink, maxWidth: 720,
                    fontStyle: "italic", margin: "8px 0 6px" }}>
          {intro}
        </p>
      )}
      {assumptionLine && (
        <p data-testid="anything-assumptions"
           style={{ fontSize: 12.5, color: T.ink3, margin: "0 0 8px",
                    fontFamily: "'Inter', sans-serif" }}>
          House assumptions — {assumptionLine}
        </p>
      )}
      {cards.length > 0 && (
        <p data-testid="anything-price-disclaimer"
           style={{ fontSize: 12, color: T.ink3, fontStyle: "italic",
                    margin: "0 0 8px", fontFamily: "'Inter', sans-serif" }}>
          *Prices are indicative — a live Concierge® confirms availability and
          the final amount before anything is arranged.
        </p>
      )}

      {cards.length === 0 ? (
        <div data-testid="anything-empty"
             style={{ background: T.card, border: `1px solid ${T.brassSoft}`,
                      borderRadius: 16, padding: "32px", margin: "16px 0",
                      maxWidth: 620 }}>
          <p style={{ letterSpacing: "0.14em", fontSize: 10, color: T.brass,
                      textTransform: "uppercase", fontWeight: 700,
                      margin: "0 0 8px", fontFamily: "'Inter', sans-serif" }}>
            The Impossible Asks Desk
          </p>
          <p style={{ fontSize: 18, margin: "0 0 6px" }}>
            This one needs a human.
          </p>
          <p style={{ fontSize: 14, color: T.ink2, margin: "0 0 18px",
                      lineHeight: 1.55, fontFamily: "'Inter', sans-serif" }}>
            Nothing worthy surfaced on the first pass — which usually means
            it's the kind of ask the desk enjoys most. Hand it over and a
            Concierge® takes it personally.
          </p>
          <button
            data-testid="anything-impossible-btn"
            type="button" onClick={onImpossible}
            style={{ background: T.ink, color: T.bg, border: 0, borderRadius: 999,
                     padding: "13px 26px", fontSize: 13, fontWeight: 600,
                     letterSpacing: "0.06em", textTransform: "uppercase",
                     cursor: "pointer", fontFamily: "'Inter', sans-serif" }}>
            Hand it to the desk →
          </button>
        </div>
      ) : (
        <>
          {gesture && cards.length > 1 && (
            <button
              data-testid="anything-gesture-arrange"
              type="button"
              onClick={() => onArrangeAll(cards.filter((c) => c.kind !== "pillar"))}
              style={{ marginTop: 14, background: T.ink, color: T.bg, border: 0,
                       borderRadius: 999, padding: "14px 28px", fontSize: 13,
                       fontWeight: 600, letterSpacing: "0.06em",
                       textTransform: "uppercase", cursor: "pointer",
                       fontFamily: "'Inter', sans-serif" }}>
              ✦ Concierge® arrange the whole gesture →
            </button>
          )}
          <div style={{ display: "grid", marginTop: 18,
                      gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
                      gap: 18 }}>
          {cards.map((c) => (
            <RecCard key={c.rec_id} card={c}
                     isSelected={!!selected[c.rec_id]}
                     onToggleSelect={onToggleSelect}
                     onDetail={onDetail} onArrange={onArrange} />
          ))}
          </div>
        </>
      )}

      <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginTop: 32,
                    alignItems: "center" }}>
        {hasMore && (
          <button
            data-testid="anything-show-more"
            type="button" onClick={onMore} disabled={moreLoading}
            style={{ background: T.ink, color: T.bg, border: 0, borderRadius: 999,
                     padding: "13px 26px", fontSize: 13, fontWeight: 600,
                     letterSpacing: "0.06em", textTransform: "uppercase",
                     cursor: moreLoading ? "wait" : "pointer",
                     opacity: moreLoading ? 0.6 : 1,
                     fontFamily: "'Inter', sans-serif" }}>
            {moreLoading ? "Fetching the next round…" : "Show me more →"}
          </button>
        )}
        <button
          data-testid="anything-ask-again"
          type="button" onClick={onReset}
          style={{ background: "transparent", color: T.ink2,
                   border: `1px solid ${T.line2}`, borderRadius: 999,
                   padding: "12px 24px", fontSize: 13, cursor: "pointer",
                   fontFamily: "'Inter', sans-serif" }}>
          ← Ask for something else
        </button>
      </div>
    </section>
  );
}

function RecCard({ card, isSelected, onToggleSelect, onDetail, onArrange }) {
  const meta = [
    card.price_display || (card.kind === "service" ? "the desk quotes this personally" : null),
    card.rating ? `★ ${card.rating}${card.reviews ? ` (${card.reviews})` : ""}` : null,
    card.category, card.area, card.when, card.venue,
  ].filter(Boolean).join(" · ");

  if (card.kind === "pillar") {
    return (
      <Link to={card.href || "/stay"}
            data-testid={`anything-card-pillar-${card.rec_id}`}
            style={{ textDecoration: "none", color: T.ink, background: T.card,
                     border: `1px dashed ${T.line2}`, borderRadius: 16,
                     padding: "22px", display: "flex", flexDirection: "column", gap: 8 }}>
        <span style={{ letterSpacing: "0.14em", fontSize: 10, color: T.brass,
                       textTransform: "uppercase", fontWeight: 700,
                       fontFamily: "'Inter', sans-serif" }}>
          {KIND_LABEL.pillar}
        </span>
        <span style={{ fontSize: 17, fontWeight: 500, lineHeight: 1.3 }}>{card.title}</span>
        <span style={{ fontSize: 13, color: T.ink2, fontFamily: "'Inter', sans-serif" }}>
          {card.note || "Continue in Stays — live rates for your dates"} →
        </span>
      </Link>
    );
  }

  return (
    <div data-testid={`anything-card-${card.rec_id}`}
         onClick={() => onDetail(card)}
         role="button"
         style={{ background: T.card, border: `1px solid ${T.line}`,
                  borderRadius: 16, overflow: "hidden", cursor: "pointer",
                  display: "flex", flexDirection: "column",
                  transition: "transform 160ms ease, box-shadow 160ms ease" }}
         onMouseEnter={(e) => {
           e.currentTarget.style.transform = "translateY(-2px)";
           e.currentTarget.style.boxShadow = "0 12px 28px rgba(33,28,21,0.09)";
         }}
         onMouseLeave={(e) => {
           e.currentTarget.style.transform = "translateY(0)";
           e.currentTarget.style.boxShadow = "none";
         }}>
      {card.image && (
        <div style={{ height: 168, background: "#EAE3D4", overflow: "hidden" }}>
          <img src={card.image} alt={card.title || ""}
               style={{ width: "100%", height: "100%", objectFit: "cover" }}
               onError={(e) => { e.currentTarget.parentElement.style.display = "none"; }} />
        </div>
      )}
      <div style={{ padding: "18px 20px 20px", display: "flex",
                    flexDirection: "column", flex: 1 }}>
        <span style={{ letterSpacing: "0.14em", fontSize: 10, color: T.brass,
                       textTransform: "uppercase", fontWeight: 700, marginBottom: 8,
                       fontFamily: "'Inter', sans-serif" }}>
          {KIND_LABEL[card.kind] || card.kind}
        </span>
        <h3 style={{ fontSize: 17, fontWeight: 500, margin: "0 0 6px", lineHeight: 1.3 }}>
          {card.title}
        </h3>
        {card.note && (
          <p style={{ fontSize: 13.5, color: T.ink2, fontStyle: "italic",
                      lineHeight: 1.45, margin: "0 0 8px" }}>
            {card.note}
          </p>
        )}
        {meta && (
          <p style={{ fontSize: 12.5, color: T.ink3, margin: "0 0 14px",
                      fontFamily: "'Inter', sans-serif" }}>
            {meta}
          </p>
        )}
        <div style={{ marginTop: "auto", display: "flex", gap: 8,
                      alignItems: "center", flexWrap: "wrap" }}>
          <button
            data-testid={`anything-arrange-${card.rec_id}`}
            type="button"
            onClick={(e) => { e.stopPropagation(); onArrange(card); }}
            style={{ background: T.ink, color: T.bg, border: 0, borderRadius: 999,
                     padding: "10px 20px", fontSize: 12.5, fontWeight: 600,
                     letterSpacing: "0.06em", textTransform: "uppercase",
                     cursor: "pointer", fontFamily: "'Inter', sans-serif" }}>
            Concierge® arrange it →
          </button>
          <button
            data-testid={`anything-select-${card.rec_id}`}
            type="button"
            onClick={(e) => { e.stopPropagation(); onToggleSelect(card); }}
            title={isSelected ? "Remove from brief" : "Add to a combined brief"}
            style={{ background: isSelected ? T.brassSoft : "transparent",
                     color: isSelected ? T.ink : T.ink2,
                     border: `1px solid ${isSelected ? T.brassSoft : T.line2}`,
                     borderRadius: 999, padding: "9px 16px", fontSize: 12,
                     fontWeight: 600, cursor: "pointer",
                     fontFamily: "'Inter', sans-serif" }}>
            {isSelected ? "✓ In brief" : "＋ Add"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Detail sheet · /fit-style right drawer, white-label safe ─────
function DetailSheet({ card, siblings, onSwitch, onClose, onArrange }) {
  const [why, setWhy] = useState(null);
  const [whyLoading, setWhyLoading] = useState(false);

  useEffect(() => {
    let alive = true;
    setWhy(null);
    setWhyLoading(true);
    fetch(`${API}/api/anything/why`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ rec_id: card.rec_id }),
    })
      .then((r) => r.json())
      .then((j) => { if (alive) { setWhy(j?.why || null); setWhyLoading(false); } })
      .catch(() => { if (alive) setWhyLoading(false); });
    return () => { alive = false; };
  }, [card.rec_id]);

  const metaPills = [
    card.price_display,
    card.rating ? `★ ${card.rating}${card.reviews ? ` (${card.reviews})` : ""}` : null,
    card.category, card.area, card.when, card.venue,
    card.delivery,
  ].filter(Boolean);
  const blurb = card.snippet || card.description || null;
  return (
    <>
      <div data-testid="anything-detail-backdrop" onClick={onClose}
           style={{ position: "fixed", inset: 0, background: "rgba(15,12,8,0.38)",
                    zIndex: 80 }} />
      <aside
        data-testid="anything-detail-sheet"
        role="dialog" aria-modal="true"
        style={{ position: "fixed", top: 0, right: 0, bottom: 0,
                 width: "min(520px, 94vw)", background: T.card,
                 zIndex: 85, display: "flex", flexDirection: "column",
                 boxShadow: "-24px 0 64px rgba(15,12,8,0.28)",
                 animation: "lc-anything-slidein 240ms ease" }}>
        <style>{`@keyframes lc-anything-slidein {
          from { transform: translateX(40px); opacity: 0; }
          to   { transform: translateX(0);    opacity: 1; } }
        @keyframes lc-anything-pulse {
          0%,100% { opacity: .45; }
          50%     { opacity: 1; } }`}</style>

        {/* Top bar */}
        <div style={{ display: "flex", alignItems: "center",
                      justifyContent: "space-between",
                      padding: "14px 20px", borderBottom: `1px solid ${T.line}` }}>
          <span style={{ letterSpacing: "0.16em", fontSize: 10, color: T.brass,
                         textTransform: "uppercase", fontWeight: 700,
                         fontFamily: "'Inter', sans-serif" }}>
            {KIND_LABEL[card.kind] || card.kind}
          </span>
          <button data-testid="anything-detail-close" type="button" onClick={onClose}
                  aria-label="Close"
                  style={{ background: "transparent", border: 0, cursor: "pointer",
                           fontSize: 20, color: T.ink2, lineHeight: 1 }}>
            ×
          </button>
        </div>

        {/* Scrollable body */}
        <div style={{ overflowY: "auto", flex: 1 }}>
          {card.image && (
            <div style={{ height: 300, background: "#EAE3D4", overflow: "hidden" }}>
              <img src={card.image} alt={card.title || ""}
                   style={{ width: "100%", height: "100%",
                            objectFit: card.kind === "shopping" ? "contain" : "cover" }}
                   onError={(e) => { e.currentTarget.parentElement.style.display = "none"; }} />
            </div>
          )}
          <div style={{ padding: "20px 24px 24px" }}>
            <p style={{ letterSpacing: "0.16em", fontSize: 10, color: T.brass,
                        textTransform: "uppercase", fontWeight: 700,
                        margin: "0 0 10px", fontFamily: "'Inter', sans-serif" }}>
              ✦ Through your Concierge®
            </p>
            <h2 style={{ fontFamily: "'Fraunces', Georgia, serif",
                         fontSize: 26, fontWeight: 500, lineHeight: 1.18,
                         margin: "0 0 12px", color: T.ink }}>
              {card.title}
            </h2>
            {metaPills.length > 0 && (
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8,
                            marginBottom: 14, fontFamily: "'Inter', sans-serif" }}>
                {metaPills.map((m) => (
                  <span key={m}
                        style={{ border: `1px solid ${T.line2}`, borderRadius: 999,
                                 padding: "5px 12px", fontSize: 12, color: T.ink2 }}>
                    {m}
                  </span>
                ))}
              </div>
            )}
            {card.note && (
              <p data-testid="anything-detail-note"
                 style={{ fontSize: 16, color: T.ink, fontStyle: "italic",
                          lineHeight: 1.55, margin: "0 0 12px" }}>
                “{card.note}”
              </p>
            )}
            {(whyLoading || why) && (
              <div data-testid="anything-detail-why"
                   style={{ background: T.bg, border: `1px solid ${T.line}`,
                            borderRadius: 12, padding: "16px 18px",
                            margin: "0 0 14px" }}>
                <p style={{ letterSpacing: "0.14em", fontSize: 10, color: T.brass,
                            textTransform: "uppercase", fontWeight: 700,
                            margin: "0 0 8px", fontFamily: "'Inter', sans-serif" }}>
                  Why the Concierge® recommends it
                </p>
                {whyLoading ? (
                  <p style={{ fontSize: 14, color: T.ink3, fontStyle: "italic",
                              margin: 0, animation: "lc-anything-pulse 1.4s ease-in-out infinite" }}>
                    The Concierge is considering…
                  </p>
                ) : (
                  <p style={{ fontFamily: "'Fraunces', Georgia, serif",
                              fontSize: 15.5, color: T.ink, lineHeight: 1.6,
                              margin: 0 }}>
                    {why}
                  </p>
                )}
              </div>
            )}
            {blurb && (
              <p data-testid="anything-detail-blurb"
                 style={{ fontSize: 14, color: T.ink2, lineHeight: 1.6,
                          margin: "0 0 16px", fontFamily: "'Inter', sans-serif" }}>
                {blurb}
              </p>
            )}
            <p style={{ fontSize: 12.5, color: T.ink3, lineHeight: 1.55,
                        margin: "0 0 20px", fontFamily: "'Inter', sans-serif" }}>
              The house sources this directly — no links, no checkout. Tap
              arrange and a Concierge® confirms details on WhatsApp.
              {card.price_display && (
                <em style={{ display: "block", marginTop: 6 }}>
                  *Price is indicative — your Concierge® confirms the final
                  amount before anything is arranged.
                </em>
              )}
            </p>

            {siblings && siblings.length > 0 && (
              <div data-testid="anything-detail-similar" style={{ marginTop: 4 }}>
                <p style={{ letterSpacing: "0.14em", fontSize: 10, color: T.ink3,
                            textTransform: "uppercase", fontWeight: 700,
                            margin: "0 0 10px", fontFamily: "'Inter', sans-serif" }}>
                  More like this
                </p>
                <div style={{ display: "flex", gap: 10, overflowX: "auto",
                              paddingBottom: 6 }}>
                  {siblings.map((s) => (
                    <button key={s.rec_id} type="button"
                            data-testid={`anything-similar-${s.rec_id}`}
                            onClick={() => onSwitch(s)}
                            style={{ flexShrink: 0, width: 120, textAlign: "left",
                                     background: T.bg, border: `1px solid ${T.line}`,
                                     borderRadius: 12, padding: 0, cursor: "pointer",
                                     overflow: "hidden" }}>
                      {s.image && (
                        <div style={{ height: 76, background: "#EAE3D4",
                                      overflow: "hidden" }}>
                          <img src={s.image} alt=""
                               style={{ width: "100%", height: "100%",
                                        objectFit: "cover" }}
                               onError={(e) => { e.currentTarget.parentElement.style.display = "none"; }} />
                        </div>
                      )}
                      <span style={{ display: "block", padding: "8px 10px",
                                     fontSize: 11.5, lineHeight: 1.3, color: T.ink,
                                     fontFamily: "'Inter', sans-serif" }}>
                        {(s.title || "").length > 44
                          ? (s.title || "").slice(0, 41) + "…" : s.title}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Sticky arrange footer */}
        <div style={{ padding: "14px 20px", borderTop: `1px solid ${T.line}`,
                      display: "flex", gap: 10, alignItems: "center",
                      justifyContent: "space-between" }}>
          {card.price_display ? (
            <span style={{ fontFamily: "'Fraunces', Georgia, serif",
                           fontSize: 18, color: T.ink }}>
              {card.price_display}
            </span>
          ) : card.kind === "service" ? (
            <span style={{ fontSize: 12.5, color: T.ink3, fontStyle: "italic",
                           fontFamily: "'Inter', sans-serif" }}>
              Quoted personally by the desk
            </span>
          ) : <span />}
          <button
            data-testid="anything-detail-arrange"
            type="button"
            onClick={() => onArrange(card)}
            style={{ background: T.ink, color: T.bg, border: 0, borderRadius: 999,
                     padding: "13px 26px", fontSize: 13, fontWeight: 600,
                     letterSpacing: "0.06em", textTransform: "uppercase",
                     cursor: "pointer", fontFamily: "'Inter', sans-serif" }}>
            Concierge® arrange it →
          </button>
        </div>
      </aside>
    </>
  );
}

// ── Arrange modal · one brief, one or many finds ────────────────
function ArrangeModal({ cards, embedMember, onClose, onSuccess, onError }) {
  const [name,  setName]  = useState(embedMember?.name  || "");
  const [phone, setPhone] = useState(embedMember?.phone || "");
  const [email, setEmail] = useState(embedMember?.email || "");
  const [note,  setNote]  = useState("");
  const [sending, setSending] = useState(false);
  const [delivery, setDelivery] = useState(false);
  const [recipient, setRecipient] = useState("");
  const [address, setAddress]     = useState("");
  const [date, setDate]           = useState("");
  const [message, setMessage]     = useState("");
  const valid = name.trim().length >= 1 && phone.trim().length >= 5;
  const multi = cards.length > 1;

  const submit = async () => {
    if (!valid || sending) return;
    setSending(true);
    try {
      const r = await fetch(`${API}/api/anything/arrange`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          rec_ids: cards.map((c) => c.rec_id),
          name: name.trim(),
          phone: phone.trim(),
          email: email.trim() || null,
          note: note.trim() || null,
          recipient_name: delivery ? recipient.trim() || null : null,
          delivery_address: delivery ? address.trim() || null : null,
          delivery_date: delivery ? date.trim() || null : null,
          card_message: delivery ? message.trim() || null : null,
        }),
      });
      const j = await r.json();
      if (r.ok && j?.ok) onSuccess(j.ref);
      else onError(j?.detail || "We couldn't send that just now. Please try again.");
    } catch (_) {
      onError("Network hiccup — please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <div data-testid="anything-modal-backdrop" onClick={onClose}
           style={{ position: "fixed", inset: 0, background: "rgba(15,12,8,0.44)",
                    zIndex: 90 }} />
      <div data-testid="anything-arrange-modal" role="dialog" aria-modal="true"
           style={{ position: "fixed", top: "50%", left: "50%",
                    transform: "translate(-50%, -50%)", width: "min(520px, 92vw)",
                    maxHeight: "90vh", overflowY: "auto", background: T.card,
                    borderRadius: 16, padding: "28px 30px",
                    boxShadow: "0 24px 64px rgba(15,12,8,0.32)", zIndex: 100,
                    fontFamily: "'Inter', sans-serif" }}>
        <p style={{ letterSpacing: "0.14em", fontSize: 10, color: T.brass,
                    textTransform: "uppercase", margin: "0 0 6px", fontWeight: 700 }}>
          Concierge® arrange {multi ? "all" : "it"}
        </p>
        <h2 style={{ fontFamily: "'Fraunces', Georgia, serif", fontSize: 24,
                     fontWeight: 500, margin: "0 0 6px", lineHeight: 1.2, color: T.ink }}>
          {multi ? `${cards.length} finds · one brief` : cards[0].title}
        </h2>
        {multi && (
          <ul data-testid="anything-arrange-items"
              style={{ margin: "0 0 10px", padding: "0 0 0 18px",
                       fontSize: 13, color: T.ink2, lineHeight: 1.6 }}>
            {cards.map((c) => (
              <li key={c.rec_id}>
                {c.title}{c.price_display ? ` — ${c.price_display}` : ""}
              </li>
            ))}
          </ul>
        )}
        <p style={{ fontSize: 13, color: T.ink2, margin: "0 0 6px", lineHeight: 1.5 }}>
          The desk will source {multi ? "everything" : "it"}, confirm the details
          and come back to you on WhatsApp. No links, no checkout — the house
          handles it.
        </p>
        <p data-testid="anything-arrange-price-note"
           style={{ fontSize: 11.5, color: T.ink3, fontStyle: "italic",
                    margin: "0 0 18px", lineHeight: 1.5 }}>
          *Prices shown are indicative — your Concierge® confirms availability
          and the final amount with you before anything is charged.
        </p>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <Field label="Name" required>
            <input value={name} onChange={(e) => setName(e.target.value)}
                   data-testid="anything-arrange-name" style={inputStyle} />
          </Field>
          <Field label="WhatsApp" required>
            <input value={phone} onChange={(e) => setPhone(e.target.value)}
                   placeholder="+91…"
                   data-testid="anything-arrange-phone" style={inputStyle} />
          </Field>
        </div>
        <Field label="Email">
          <input value={email} onChange={(e) => setEmail(e.target.value)}
                 data-testid="anything-arrange-email" style={inputStyle} />
        </Field>
        <Field label="Anything to add?">
          <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3}
                    maxLength={500}
                    placeholder="Colour, size, deadline, gift-wrap…"
                    data-testid="anything-arrange-note"
                    style={{ ...inputStyle, resize: "vertical" }} />
        </Field>

        {/* Delivery brief · florist-grade, optional */}
        <button
          data-testid="anything-arrange-delivery-toggle"
          type="button"
          onClick={() => setDelivery(!delivery)}
          style={{ background: "transparent", border: 0, padding: 0,
                   marginBottom: delivery ? 14 : 4, cursor: "pointer",
                   display: "flex", alignItems: "center", gap: 8,
                   color: T.brass, fontSize: 12.5, fontWeight: 700,
                   letterSpacing: "0.1em", textTransform: "uppercase",
                   fontFamily: "'Inter', sans-serif" }}>
          <span style={{ display: "inline-block",
                         transform: delivery ? "rotate(90deg)" : "none",
                         transition: "transform 160ms ease" }}>›</span>
          Sending it to someone? Add delivery details
        </button>
        {delivery && (
          <div data-testid="anything-arrange-delivery-section"
               style={{ background: T.bg, border: `1px solid ${T.line}`,
                        borderRadius: 12, padding: "16px 16px 6px",
                        marginBottom: 14 }}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <Field label="Recipient name">
                <input value={recipient} onChange={(e) => setRecipient(e.target.value)}
                       placeholder="Who receives it"
                       data-testid="anything-arrange-recipient" style={inputStyle} />
              </Field>
              <Field label="Delivery date">
                <input type="date" value={date} onChange={(e) => setDate(e.target.value)}
                       data-testid="anything-arrange-date" style={inputStyle} />
              </Field>
            </div>
            <Field label="Delivery address">
              <textarea value={address} onChange={(e) => setAddress(e.target.value)}
                        rows={2} maxLength={400}
                        placeholder="Street, apartment, city, country, ZIP/postcode"
                        data-testid="anything-arrange-address"
                        style={{ ...inputStyle, resize: "vertical" }} />
            </Field>
            <Field label="Card message">
              <textarea value={message} onChange={(e) => setMessage(e.target.value)}
                        rows={2} maxLength={300}
                        placeholder="“Happy anniversary, with all my love…”"
                        data-testid="anything-arrange-message"
                        style={{ ...inputStyle, resize: "vertical" }} />
            </Field>
          </div>
        )}

        <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 20 }}>
          <button data-testid="anything-arrange-cancel" type="button" onClick={onClose}
                  style={{ background: "transparent", color: T.ink2,
                           border: `1px solid ${T.line2}`, borderRadius: 999,
                           padding: "11px 20px", fontSize: 13, cursor: "pointer" }}>
            Cancel
          </button>
          <button data-testid="anything-arrange-submit" type="button"
                  onClick={submit} disabled={!valid || sending}
                  style={{ background: T.ink, color: T.bg, border: 0, borderRadius: 999,
                           padding: "11px 22px", fontSize: 13, fontWeight: 600,
                           letterSpacing: "0.06em", textTransform: "uppercase",
                           cursor: sending ? "wait" : "pointer",
                           opacity: !valid || sending ? 0.5 : 1 }}>
            {sending ? "Sending…" : "Send to the desk →"}
          </button>
        </div>
      </div>
    </>
  );
}

// ── Impossible Asks Desk · escalation modal ─────────────────────
function ImpossibleModal({ query, embedMember, onClose, onSuccess, onError }) {
  const [name,  setName]  = useState(embedMember?.name  || "");
  const [phone, setPhone] = useState(embedMember?.phone || "");
  const [email, setEmail] = useState(embedMember?.email || "");
  const [note,  setNote]  = useState("");
  const [sending, setSending] = useState(false);
  const valid = name.trim().length >= 1 && phone.trim().length >= 5;

  const submit = async () => {
    if (!valid || sending) return;
    setSending(true);
    try {
      const r = await fetch(`${API}/api/anything/impossible`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          query,
          name: name.trim(),
          phone: phone.trim(),
          email: email.trim() || null,
          note: note.trim() || null,
        }),
      });
      const j = await r.json();
      if (r.ok && j?.ok) onSuccess(j.ref);
      else onError(j?.detail || "We couldn't send that just now. Please try again.");
    } catch (_) {
      onError("Network hiccup — please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <div data-testid="anything-impossible-backdrop" onClick={onClose}
           style={{ position: "fixed", inset: 0, background: "rgba(15,12,8,0.44)",
                    zIndex: 90 }} />
      <div data-testid="anything-impossible-modal" role="dialog" aria-modal="true"
           style={{ position: "fixed", top: "50%", left: "50%",
                    transform: "translate(-50%, -50%)", width: "min(520px, 92vw)",
                    maxHeight: "90vh", overflowY: "auto", background: T.card,
                    borderRadius: 16, padding: "28px 30px",
                    boxShadow: "0 24px 64px rgba(15,12,8,0.32)", zIndex: 100,
                    fontFamily: "'Inter', sans-serif" }}>
        <p style={{ letterSpacing: "0.14em", fontSize: 10, color: T.brass,
                    textTransform: "uppercase", margin: "0 0 6px", fontWeight: 700 }}>
          The Impossible Asks Desk
        </p>
        <h2 style={{ fontFamily: "'Fraunces', Georgia, serif", fontSize: 24,
                     fontWeight: 500, margin: "0 0 6px", lineHeight: 1.25, color: T.ink }}>
          “{query}”
        </h2>
        <p style={{ fontSize: 13, color: T.ink2, margin: "0 0 18px", lineHeight: 1.5 }}>
          A Concierge® takes this personally — sourced by hand, not by engine.
          Expect a WhatsApp within the hour.
        </p>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <Field label="Name" required>
            <input value={name} onChange={(e) => setName(e.target.value)}
                   data-testid="anything-impossible-name" style={inputStyle} />
          </Field>
          <Field label="WhatsApp" required>
            <input value={phone} onChange={(e) => setPhone(e.target.value)}
                   placeholder="+91…"
                   data-testid="anything-impossible-phone" style={inputStyle} />
          </Field>
        </div>
        <Field label="Email">
          <input value={email} onChange={(e) => setEmail(e.target.value)}
                 data-testid="anything-impossible-email" style={inputStyle} />
        </Field>
        <Field label="Anything that helps the hunt?">
          <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3}
                    maxLength={500}
                    placeholder="Deadline, exact model, a photo reference the desk can chase…"
                    data-testid="anything-impossible-note"
                    style={{ ...inputStyle, resize: "vertical" }} />
        </Field>
        <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 20 }}>
          <button data-testid="anything-impossible-cancel" type="button" onClick={onClose}
                  style={{ background: "transparent", color: T.ink2,
                           border: `1px solid ${T.line2}`, borderRadius: 999,
                           padding: "11px 20px", fontSize: 13, cursor: "pointer" }}>
            Cancel
          </button>
          <button data-testid="anything-impossible-submit" type="button"
                  onClick={submit} disabled={!valid || sending}
                  style={{ background: T.ink, color: T.bg, border: 0, borderRadius: 999,
                           padding: "11px 22px", fontSize: 13, fontWeight: 600,
                           letterSpacing: "0.06em", textTransform: "uppercase",
                           cursor: sending ? "wait" : "pointer",
                           opacity: !valid || sending ? 0.5 : 1 }}>
            {sending ? "Sending…" : "Hand it to the desk →"}
          </button>
        </div>
      </div>
    </>
  );
}

function Field({ label, required, children }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <label style={{ display: "block", letterSpacing: "0.12em", fontSize: 10.5,
                      color: T.ink3, textTransform: "uppercase", marginBottom: 6,
                      fontWeight: 700 }}>
        {label}{required ? " *" : ""}
      </label>
      {children}
    </div>
  );
}

const inputStyle = {
  width: "100%", padding: "10px 14px", fontSize: 14,
  borderRadius: 8, border: `1px solid ${T.line2}`,
  background: T.card, color: T.ink,
  fontFamily: "'Inter', sans-serif", outline: "none",
};
